/**
 * ImageWithFallback - Component for displaying images with fallback containers
 *
 * Supports both avatar mode (circular) and regular image mode (rectangular)
 *
 * @example
 * ```tsx
 * <ImageWithFallback
 *   src={teacher.imageUrl}
 *   alt={teacher.name}
 *   fallbackType="avatar"
 *   size="120px"
 * />
 * <ImageWithFallback
 *   src={class.imageUrl}
 *   alt={class.name}
 *   fallbackType="image"
 *   width="100%"
 *   height="280px"
 * />
 * ```
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { HiOutlinePhoto } from "react-icons/hi2";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Text } from "../ui/Text";

export interface ImageWithFallbackProps {
  /**
   * Image source URL
   */
  src?: string | null;
  /**
   * Alt text for the image
   */
  alt: string;
  /**
   * Type of fallback: "avatar" for circular avatar, "image" for rectangular
   */
  fallbackType?: "avatar" | "image";
  /**
   * Size for avatar (width and height)
   */
  size?: string;
  /**
   * Width for image
   */
  width?: string | number | { base?: string | number; sm?: string | number; md?: string | number; lg?: string | number; xl?: string | number; "2xl"?: string | number };
  /**
   * Height for image
   */
  height?: string | number | { base?: string | number; sm?: string | number; md?: string | number; lg?: string | number; xl?: string | number; "2xl"?: string | number };
  /**
   * Border radius override
   */
  borderRadius?: string;
  /**
   * Additional className
   */
  className?: string;
  /**
   * Object fit style
   */
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
  /**
   * Fallback text (defaults to initials or "Image")
   */
  fallbackText?: string;
  /**
   * Priority loading for LCP images (above the fold)
   */
  priority?: boolean;
  /**
   * Called when the image fails to load (e.g. expired signed URL).
   */
  onImageError?: () => void;
  /**
   * When false, empty image fallback omits the photo icon and label (border/bg from parent).
   */
  showFallbackIcon?: boolean;
  /**
   * When false, keep the photo icon but hide the "Image" label (homepage covers).
   */
  showFallbackLabel?: boolean;
}

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export const ImageWithFallback = ({
  src,
  alt,
  fallbackType = "image",
  size,
  width,
  height,
  borderRadius,
  className,
  objectFit = "cover",
  fallbackText,
  priority = false,
  onImageError,
  showFallbackIcon = true,
  showFallbackLabel = true,
}: ImageWithFallbackProps) => {
  const theme = useThemeTokens();
  const [imgError, setImgError] = useState(false);

  // No image: null, undefined, empty string, or whitespace-only → show fallback container
  const hasImage = typeof src === "string" && src.trim() !== "";
  const showImage = hasImage && !imgError;

  useEffect(() => {
    setImgError(false);
  }, [src]);

  const handleError = useCallback(() => {
    setImgError(true);
    onImageError?.();
  }, [onImageError]);

  const isAvatar = fallbackType === "avatar";
  const displaySize = size || (isAvatar ? "120px" : undefined);
  const displayWidth = width || (isAvatar ? displaySize : "100%");
  const displayHeight = height || (isAvatar ? displaySize : "280px");
  const displayBorderRadius = borderRadius || (isAvatar ? "50%" : "12px");

  const finalFallbackText = fallbackText || (isAvatar ? getInitials(alt) : "Image");

  // External URLs (API, CDN, signed URLs): use native <img> so we never hit /_next/image.
  // Next image optimizer can 404 on test/prod and crash the app; native img loads directly and onError shows fallback.
  // Only use Next Image for same-origin relative paths (starts with single "/", not "//").
  const trimmedSrc = typeof src === "string" ? src.trim() : "";
  const isRelativePath =
    trimmedSrc.length > 0 && trimmedSrc.startsWith("/") && !trimmedSrc.startsWith("//");
  const isExternalUrl =
    trimmedSrc.length > 0 && !isRelativePath;

  if (showImage) {
    const boxProps = {
      position: "relative" as const,
      width: displayWidth,
      height: displayHeight,
      borderRadius: displayBorderRadius,
      overflow: "hidden" as const,
      flexShrink: 0,
      ...(isAvatar ? { aspectRatio: 1 as const } : {}),
      className,
    };

    if (isExternalUrl) {
      return (
        <Box {...boxProps}>
          <Box
            as="img"
            src={trimmedSrc}
            alt={alt}
            onError={handleError}
            position="absolute"
            inset={0}
            w="100%"
            h="100%"
            objectFit={objectFit}
            loading={priority ? "eager" : "lazy"}
          />
        </Box>
      );
    }

    return (
      <Box {...boxProps}>
        <Image
          src={src!}
          alt={alt}
          fill
          priority={priority}
          style={{ objectFit }}
          sizes={isAvatar ? `${displaySize}` : undefined}
          onError={handleError}
        />
      </Box>
    );
  }

  // Fallback container
  return (
    <Flex
      width={displayWidth}
      height={displayHeight}
      borderRadius={displayBorderRadius}
      {...(isAvatar ? { aspectRatio: 1 } : {})}
      alignItems="center"
      justifyContent="center"
      direction="column"
      gap={2}
      bg={
        isAvatar && theme.gradientEnabled
          ? `linear-gradient(135deg, ${theme.accent.primary} 0%, ${theme.accent.mutedHex || theme.accent.muted} 100%)`
          : isAvatar
          ? theme.accent.primary
          : "transparent"
      }
      borderWidth={isAvatar ? "0" : "0"}
      borderColor={theme.borderGlass.default}
      borderStyle={isAvatar ? "solid" : "dashed"}
      flexShrink={0}
      className={className}
    >
      {isAvatar ? (
        <Text
          fontSize="2xl"
          fontWeight="400"
          color="white"
        >
          {finalFallbackText}
        </Text>
      ) : showFallbackIcon ? (
        <>
          <Box color={theme.text.tertiary} opacity={0.5}>
            <HiOutlinePhoto size={48} />
          </Box>
          {showFallbackLabel ? (
            <Text
              fontSize="xs"
              fontWeight="400"
              color={theme.text.tertiary}
              textTransform="uppercase"
              letterSpacing="0.05em"
            >
              {finalFallbackText}
            </Text>
          ) : null}
        </>
      ) : null}
    </Flex>
  );
};
